import { useState } from 'react';

import { WrappedStory } from '../components/wrapped/index.js';
import { EARLY_SLIDES } from '../components/wrapped/slides/slidesEarly.js';
import { LATE_SLIDES } from '../components/wrapped/slides/slidesLate.js';
import { WrappedShareDock } from '../components/WrappedShareDock.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { usePartyWrapped } from '../hooks/usePartyWrapped.js';
import { wrappedPath } from '../routing/paths.js';

import type { PartySlug, PartyWrapped } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type WrappedPageProps = {
  readonly slug: PartySlug;
};

const Notice = ({
  heading,
  detail,
}: {
  readonly heading: string;
  readonly detail: string;
}): ReactElement => (
  <div className="flex flex-col items-center gap-3 text-center">
    <p className="text-muted text-xs tracking-widest uppercase">The night, recapped</p>
    <h1 className="font-display text-content text-5xl sm:text-7xl">{heading}</h1>
    <p className="text-muted text-sm">{detail}</p>
  </div>
);

const Recap = ({ wrapped }: { readonly wrapped: PartyWrapped }): ReactElement => {
  const [runId, setRunId] = useState(0);

  return (
    <WrappedStory
      key={runId}
      label={`${wrapped.party.displayName} wrapped`}
      slides={[
        ...EARLY_SLIDES(wrapped),
        ...LATE_SLIDES(wrapped, () => {
          setRunId((previous) => previous + 1);
        }),
      ]}
    />
  );
};

export const WrappedPage = ({ slug }: WrappedPageProps): ReactElement => {
  const path = wrappedPath(slug);
  const wrapped = usePartyWrapped(slug);

  usePageTitle(wrapped.data?.party.displayName ?? 'Wrapped');

  return (
    <>
      <div className="fixed inset-0 z-30">
        {wrapped.data === undefined ? (
          <div className="flex h-full flex-col items-center justify-center px-6 pb-24 md:pb-0">
            {wrapped.isError ? (
              <Notice
                heading="No recap"
                detail="That party does not exist, or has nothing to show yet."
              />
            ) : (
              <Notice heading="Wrapped" detail="Counting up the night…" />
            )}
          </div>
        ) : (
          <Recap wrapped={wrapped.data} />
        )}
      </div>

      {/* Its own stacking context, so the dock's fixed controls sit above the full-screen story. */}
      <div className="relative z-40">
        <WrappedShareDock path={path} shareText={`See how ${slug} went on MixWire`} />
      </div>
    </>
  );
};
